import { HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { ResponseUtilityService } from '@zayan-erp/shared';
import { API_CONFIG, ApiResponseModel, ApiCallingService, ApiCallingV2Service } from '@zayan-erp/shared';
import { LoginActivity, LoginActivityPage, LoginActivityQuery } from '../models/login-report.models';

@Injectable({
  providedIn: 'root'
})
export class LoginReportService {
  _apiCalling = inject(ApiCallingService);
  _response = inject(ResponseUtilityService);
  readonly #http = inject(ApiCallingV2Service);
  readonly #baseUrl = inject(API_CONFIG).baseUrlV2;

  /**
   * The endpoint answers with a bare array — no total count, no pagination headers — so
   * one row past the page is requested to learn whether a next page exists, then dropped.
   * `IsValidDateRange` is not sent: it is a read-only property the backend computes itself
   * and rejects the request over, so the range is kept valid on this side instead.
   */
  getLoginActivities(query: LoginActivityQuery): Observable<LoginActivityPage> {
    let params = new HttpParams()
      .set('PageNumber', query.pageNumber)
      .set('PageSize', query.pageSize + 1)
      .set('SortBy', query.sortBy)
      .set('SortOrder', query.sortOrder);

    if (query.fromDate) {
      params = params.set('FromDate', query.fromDate);
    }
    if (query.toDate) {
      params = params.set('ToDate', query.toDate);
    }

    return this.#http
      .getData<LoginActivity[]>(`${this.#baseUrl}/loginactivity`, {
        showLoader: false,
        showMessage: false,
        options: { params }
      })
      .pipe(
        map((response: LoginActivity[] | null) => {
          const activities = response ?? [];

          return {
            activities: activities.slice(0, query.pageSize),
            hasNextPage: activities.length > query.pageSize
          };
        })
      );
  }

  /** v1, kept for the legacy admin console report. New callers want {@link getLoginActivities}. */
  getLoginActivity(fromDate: string, toDate: string): Observable<LoginActivity[]> {
    return this._apiCalling
      .getData(`loginactivity`, `?FromDate=${fromDate}&ToDate=${toDate}&PageSize=1000`, true)
      .pipe(
        map((response: ApiResponseModel<LoginActivity[]>) =>
          this._response.handleResponse<LoginActivity[]>(response, 'Success!', false)
        )
      );
  }
}
